import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Card,
  DataTable,
  Badge,
  Button,
  BlockStack,
  InlineStack,
  SkeletonBodyText,
  EmptyState,
  Modal,
  TextField,
  Checkbox,
  Text,
  Box,
  Banner,
  Thumbnail,
} from '@shopify/polaris';
import { productsApi } from '@/services';
import type { RentalProductConfig, UpdateRentalConfigDto } from '@/services/products';

/** Convert cents to display dollars */
function centsToDollars(cents: number | null): string {
  if (cents === null || cents === undefined) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

/** Parse a dollar string to cents */
function dollarsToCents(dollars: string): number | undefined {
  const val = parseFloat(dollars);
  if (isNaN(val)) return undefined;
  return Math.round(val * 100);
}

export default function ProductsList() {
  const queryClient = useQueryClient();
  const [editProduct, setEditProduct] = useState<RentalProductConfig | null>(null);
  // Default to rental-configured products only — accessories (o-rings, sleeves,
  // hoses) share the Game Ready vendor but don't belong in the rental portal.
  const [showAllProducts, setShowAllProducts] = useState(false);

  // Form state for editing
  const [formIsRental, setFormIsRental] = useState(false);
  const [formWeeklyRate, setFormWeeklyRate] = useState('');
  const [formMonthlyRate, setFormMonthlyRate] = useState('');
  const [formBilateralPrice, setFormBilateralPrice] = useState('');
  const [formWholesaleWeeklyRate, setFormWholesaleWeeklyRate] = useState('');
  const [formWholesaleMonthlyRate, setFormWholesaleMonthlyRate] = useState('');
  const [formWholesaleBilateralPrice, setFormWholesaleBilateralPrice] = useState('');
  const [formMinWeeks, setFormMinWeeks] = useState('2');

  const { data: products, isLoading, error } = useQuery({
    queryKey: ['products'],
    queryFn: () => productsApi.list(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ productId, config }: { productId: string; config: UpdateRentalConfigDto }) =>
      productsApi.updateRentalConfig(productId, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setEditProduct(null);
    },
  });

  const openEditor = useCallback((product: RentalProductConfig) => {
    setEditProduct(product);
    setFormIsRental(product.rentalConfig.isRentalProduct);
    setFormWeeklyRate(
      product.rentalConfig.weeklyRateCents
        ? (product.rentalConfig.weeklyRateCents / 100).toFixed(2)
        : '',
    );
    setFormMonthlyRate(
      product.rentalConfig.monthlyRateCents
        ? (product.rentalConfig.monthlyRateCents / 100).toFixed(2)
        : '',
    );
    setFormBilateralPrice(
      product.rentalConfig.bilateralPriceCents
        ? (product.rentalConfig.bilateralPriceCents / 100).toFixed(2)
        : '',
    );
    setFormWholesaleWeeklyRate(
      product.rentalConfig.wholesaleWeeklyRateCents
        ? (product.rentalConfig.wholesaleWeeklyRateCents / 100).toFixed(2)
        : '',
    );
    setFormWholesaleMonthlyRate(
      product.rentalConfig.wholesaleMonthlyRateCents
        ? (product.rentalConfig.wholesaleMonthlyRateCents / 100).toFixed(2)
        : '',
    );
    setFormWholesaleBilateralPrice(
      product.rentalConfig.wholesaleBilateralPriceCents
        ? (product.rentalConfig.wholesaleBilateralPriceCents / 100).toFixed(2)
        : '',
    );
    setFormMinWeeks(String(product.rentalConfig.minRentalWeeks || 2));
  }, []);

  const handleSave = useCallback(() => {
    if (!editProduct) return;

    const config: UpdateRentalConfigDto = {
      isRentalProduct: formIsRental,
    };

    const weekly = dollarsToCents(formWeeklyRate);
    if (weekly !== undefined) config.weeklyRateCents = weekly;

    const monthly = dollarsToCents(formMonthlyRate);
    if (monthly !== undefined) config.monthlyRateCents = monthly;

    const bilateral = dollarsToCents(formBilateralPrice);
    if (bilateral !== undefined) config.bilateralPriceCents = bilateral;

    const wholesaleWeekly = dollarsToCents(formWholesaleWeeklyRate);
    if (wholesaleWeekly !== undefined) config.wholesaleWeeklyRateCents = wholesaleWeekly;

    const wholesaleMonthly = dollarsToCents(formWholesaleMonthlyRate);
    if (wholesaleMonthly !== undefined) config.wholesaleMonthlyRateCents = wholesaleMonthly;

    const wholesaleBilateral = dollarsToCents(formWholesaleBilateralPrice);
    if (wholesaleBilateral !== undefined) config.wholesaleBilateralPriceCents = wholesaleBilateral;

    const minWeeks = parseInt(formMinWeeks, 10);
    if (!isNaN(minWeeks) && minWeeks >= 1) config.minRentalWeeks = minWeeks;

    updateMutation.mutate({
      productId: editProduct.shopifyProductId,
      config,
    });
  }, [editProduct, formIsRental, formWeeklyRate, formMonthlyRate, formBilateralPrice, formWholesaleWeeklyRate, formWholesaleMonthlyRate, formWholesaleBilateralPrice, formMinWeeks, updateMutation]);

  if (isLoading) {
    return (
      <Page title="Products">
        <Card>
          <SkeletonBodyText lines={8} />
        </Card>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Products">
        <Banner tone="critical">
          <p>
            Failed to load products. Make sure the app has been authenticated —
            open it from the Shopify Admin to trigger the token exchange.
          </p>
        </Banner>
      </Page>
    );
  }

  if (!products || products.length === 0) {
    return (
      <Page title="Products">
        <Card>
          <EmptyState
            heading="No products found"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>
              Products from your Shopify store will appear here once the app is
              authenticated.
            </p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  const visibleProducts = showAllProducts
    ? products
    : products.filter((p) => p.rentalConfig.isRentalProduct);

  const rows = visibleProducts.map((product) => {
    const primarySku = product.variants[0]?.sku || '—';
    const variantPrice = product.variants[0]?.price
      ? `$${parseFloat(product.variants[0].price).toFixed(2)}`
      : '—';

    return [
      <InlineStack gap="300" blockAlign="center" key={product.shopifyProductId}>
        {product.imageUrl ? (
          <Thumbnail source={product.imageUrl} alt={product.title} size="small" />
        ) : null}
        <Text variant="bodyMd" as="span" fontWeight="semibold">
          {product.title}
        </Text>
      </InlineStack>,
      primarySku,
      variantPrice,
      product.rentalConfig.isRentalProduct ? (
        <Badge tone="success">Rental</Badge>
      ) : (
        <Badge>Standard</Badge>
      ),
      centsToDollars(product.rentalConfig.weeklyRateCents),
      centsToDollars(product.rentalConfig.monthlyRateCents),
      centsToDollars(product.rentalConfig.bilateralPriceCents),
      centsToDollars(product.rentalConfig.wholesaleWeeklyRateCents),
      centsToDollars(product.rentalConfig.wholesaleBilateralPriceCents),
      product.rentalConfig.minRentalWeeks,
      <Button key="edit" size="slim" onClick={() => openEditor(product)}>
        Configure
      </Button>,
    ];
  });

  return (
    <Page title="Products" subtitle="Manage rental pricing configuration via Shopify product metafields">
      <BlockStack gap="400">
        <Card>
          <Box paddingBlockEnd="300">
            <InlineStack align="space-between" blockAlign="center">
              <Checkbox
                label="Show all Game Ready products (incl. accessories)"
                helpText="Turn on to configure a new product as a rental. Off shows rental products only."
                checked={showAllProducts}
                onChange={setShowAllProducts}
              />
              <Text as="span" tone="subdued">
                {visibleProducts.length} of {products.length} products
              </Text>
            </InlineStack>
          </Box>
          {visibleProducts.length === 0 && (
            <Box paddingBlockEnd="300">
              <Banner tone="info">
                <p>
                  No rental products configured yet. Toggle &quot;Show all Game Ready
                  products&quot; above and mark the rental machines via Configure.
                </p>
              </Banner>
            </Box>
          )}
          <DataTable
            columnContentTypes={[
              'text',
              'text',
              'text',
              'text',
              'text',
              'text',
              'text',
              'text',
              'text',
              'numeric',
              'text',
            ]}
            headings={[
              'Product',
              'SKU',
              'Base Price',
              'Type',
              'Retail/Week',
              'Retail/Month',
              'Retail Bilateral',
              'Wholesale/Week',
              'Wholesale Bilateral',
              'Min Weeks',
              '',
            ]}
            rows={rows}
          />
        </Card>
      </BlockStack>

      {editProduct && (
        <Modal
          open={!!editProduct}
          onClose={() => setEditProduct(null)}
          title={`Configure: ${editProduct.title}`}
          primaryAction={{
            content: 'Save',
            onAction: handleSave,
            loading: updateMutation.isPending,
          }}
          secondaryActions={[
            { content: 'Cancel', onAction: () => setEditProduct(null) },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="400">
              {updateMutation.isError && (
                <Banner tone="critical">
                  <p>Failed to save: {(updateMutation.error as Error).message}</p>
                </Banner>
              )}

              <Checkbox
                label="Rental product"
                helpText="Enable rental pricing, duration selector, and cart transform for this product"
                checked={formIsRental}
                onChange={setFormIsRental}
              />

              {formIsRental && (
                <>
                  <TextField
                    label="Weekly rental rate (CAD)"
                    type="number"
                    prefix="$"
                    value={formWeeklyRate}
                    onChange={setFormWeeklyRate}
                    helpText="Price per week. The cart transform uses this to calculate total rental cost."
                    autoComplete="off"
                  />

                  <TextField
                    label="Monthly rental rate (CAD)"
                    type="number"
                    prefix="$"
                    value={formMonthlyRate}
                    onChange={setFormMonthlyRate}
                    helpText="Discounted rate for 4+ week rentals. Leave empty to use weekly rate for all durations."
                    autoComplete="off"
                  />

                  <TextField
                    label="Bilateral add-on price (CAD)"
                    type="number"
                    prefix="$"
                    value={formBilateralPrice}
                    onChange={setFormBilateralPrice}
                    helpText="Retail one-time charge for bilateral treatment."
                    autoComplete="off"
                  />

                  <Box padding="200" background="bg-surface-secondary" borderRadius="200">
                    <Text variant="headingSm" as="h3">Wholesale Pricing (Clinic Orders)</Text>
                  </Box>

                  <TextField
                    label="Wholesale weekly rate (CAD)"
                    type="number"
                    prefix="$"
                    value={formWholesaleWeeklyRate}
                    onChange={setFormWholesaleWeeklyRate}
                    helpText="Weekly rate for clinic/wholesale orders. e.g. $180.00"
                    autoComplete="off"
                  />

                  <TextField
                    label="Wholesale monthly rate (CAD)"
                    type="number"
                    prefix="$"
                    value={formWholesaleMonthlyRate}
                    onChange={setFormWholesaleMonthlyRate}
                    helpText="Discounted wholesale rate for 4+ week clinic orders."
                    autoComplete="off"
                  />

                  <TextField
                    label="Wholesale bilateral price (CAD)"
                    type="number"
                    prefix="$"
                    value={formWholesaleBilateralPrice}
                    onChange={setFormWholesaleBilateralPrice}
                    helpText="Wholesale one-time bilateral charge for clinic orders."
                    autoComplete="off"
                  />

                  <TextField
                    label="Minimum rental weeks"
                    type="number"
                    value={formMinWeeks}
                    onChange={setFormMinWeeks}
                    helpText="Minimum rental duration the customer can select."
                    autoComplete="off"
                  />

                  <Box padding="200" background="bg-surface-secondary" borderRadius="200">
                    <BlockStack gap="200">
                      <Text variant="headingSm" as="h3">
                        Pricing summary
                      </Text>
                      <Text variant="bodySm" as="p" tone="subdued">
                        2-week rental: {formWeeklyRate ? `$${(parseFloat(formWeeklyRate) * 2).toFixed(2)}` : '—'}
                      </Text>
                      <Text variant="bodySm" as="p" tone="subdued">
                        4-week rental:{' '}
                        {formMonthlyRate
                          ? `$${parseFloat(formMonthlyRate).toFixed(2)} (monthly rate)`
                          : formWeeklyRate
                            ? `$${(parseFloat(formWeeklyRate) * 4).toFixed(2)} (weekly × 4)`
                            : '—'}
                      </Text>
                      <Text variant="bodySm" as="p" tone="subdued">
                        8-week rental:{' '}
                        {formMonthlyRate
                          ? `$${(parseFloat(formMonthlyRate) * 2).toFixed(2)} (monthly × 2)`
                          : formWeeklyRate
                            ? `$${(parseFloat(formWeeklyRate) * 8).toFixed(2)} (weekly × 8)`
                            : '—'}
                      </Text>
                    </BlockStack>
                  </Box>
                </>
              )}
            </BlockStack>
          </Modal.Section>
        </Modal>
      )}
    </Page>
  );
}
